// MinIO servers — master admin screen.
//
// The MinIO instances a managed bucket can be provisioned on. A deployment
// normally has one configured through the environment, which needs no row here
// and stays the default; this screen registers the additional ones, so buckets
// for different clients can live on different hardware or in different regions.
//
// The root password is WRITE-ONLY. It is the most powerful credential in the
// product — it can read, delete and re-key every bucket on that server — so the
// API never returns it and editing without re-entering keeps the existing one.

import { useEffect, useState } from 'react';
import { I } from '../components/icons';
import { Modal, useToast, Empty } from '../components/ui';
import {
  fetchStorageServers, createStorageServer, updateStorageServer,
  deleteStorageServer, testStorageServer,
  ApiError, type StorageServer, type StorageServerInput,
} from '../api/client';

// Mirrors the server's rules so the operator is told while typing rather than
// after a round trip. MinIO rejects a shorter user or password itself, with a
// message that reads like a connection problem.
const MIN_USER = 3;
const MIN_PASSWORD = 8;

/** Returns why an address is unusable, or '' when it is fine. An S3 endpoint is
 *  a scheme, host and optional port — a pasted browser URL carries a path. */
function endpointProblem(label: string, v: string): string {
  if (!v) return '';
  if (!/^https?:\/\//.test(v)) return `${label} must start with http:// or https://`;
  let u: URL;
  try { u = new URL(v); } catch { return `${label} is not a valid address`; }
  if (!u.host) return `${label} is not a valid address`;
  if (u.pathname && u.pathname !== '/') return `${label} must be just the address — try ${u.protocol}//${u.host}`;
  if (u.search || u.hash) return `${label} must be just the address, without a query or #fragment`;
  return '';
}

const EMPTY: StorageServerInput = {
  name: '', endpoint: '', publicEndpoint: '', region: 'us-east-1',
  rootUser: '', rootPassword: '', note: '',
};

export function StorageServersScreen() {
  const toast = useToast();
  const [servers, setServers] = useState<StorageServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editFor, setEditFor] = useState<StorageServer | null>(null);
  const [deleteFor, setDeleteFor] = useState<StorageServer | null>(null);
  // Last reachability result per server id, so a row can show what the operator
  // just asked rather than making them read a toast that has gone.
  const [tested, setTested] = useState<Record<string, { ok: boolean; error?: string }>>({});
  const [testing, setTesting] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      setServers(await fetchStorageServers());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const test = async (s: StorageServer) => {
    setTesting(s.id);
    try {
      const res = await testStorageServer(s.id);
      setTested((p) => ({ ...p, [s.id]: res }));
      if (res.ok) toast?.success('Reachable', s.name);
      else toast?.error('Not reachable', res.error ?? s.name);
    } catch (e) {
      toast?.error('Could not test', e instanceof ApiError ? e.message : String(e));
    } finally {
      setTesting(null);
    }
  };

  const remove = async (s: StorageServer) => {
    try {
      await deleteStorageServer(s.id);
      toast?.success('Removed', s.name);
      setDeleteFor(null);
      reload();
    } catch (e) {
      // Reaching here means a bucket was created on this server since the list
      // was loaded — the row said zero and the server disagreed. Reload so the
      // count, and with it the disabled button, tells the truth.
      toast?.error('Delete failed', e instanceof ApiError ? e.message : String(e));
      setDeleteFor(null);
      reload();
    }
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Managed S3 servers</h1>
          <p className="page-sub">
            S3 servers a client's bucket can be created on (MinIO today). Pick one when creating a bucket;
            existing buckets stay on the server they were made on. Root credentials are stored
            encrypted and never shown again.
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={reload} disabled={loading}
                  title="Re-read the servers and their bucket counts">
            <I.Refresh size={13}/> {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
            <I.Plus size={14}/> Add server
          </button>
        </div>
      </div>

      <div className="metric-grid" style={{ marginBottom: 14 }}>
        <div className="metric">
          <div className="metric-label">Servers</div>
          <div className="metric-value">{servers.length}</div>
        </div>
      </div>

      {err && <div className="card"><div className="field-error" style={{ padding: 12 }}>Failed to load: {err}</div></div>}

      <div className="card">
        {loading && servers.length === 0 ? (
          <div className="muted" style={{ padding: 20, fontSize: 12.5 }}>Loading…</div>
        ) : servers.length === 0 ? (
          <Empty icon={<I.Server size={28}/>} title="No managed S3 servers registered"
                 message="Buckets are created on the server configured in this deployment's environment. Add one here to be able to choose."/>
        ) : (
          <table className="tbl">
            <thead><tr>
              <th>Name</th>
              <th>Endpoint</th>
              <th>Region</th>
              <th>Root user</th>
              <th style={{ textAlign: 'right' }}>Buckets</th>
              <th></th>
            </tr></thead>
            <tbody>
              {servers.map((s) => {
                const t = tested[s.id];
                return (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                      {s.note && <div className="muted" style={{ fontSize: 11.5 }}>{s.note}</div>}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {s.endpoint}
                      {s.publicEndpoint && s.publicEndpoint !== s.endpoint && (
                        <div className="muted" style={{ fontSize: 11.5 }}>public: {s.publicEndpoint}</div>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{s.region}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{s.rootUser}</td>
                    <td style={{ textAlign: 'right', fontSize: 12.5 }}>
                      {s.buckets > 0 ? (
                        <span data-tip="The server can't be removed while it still holds buckets — delete them from the clients' S3 tabs first, since nothing else records where that storage is."
                              style={{ borderBottom: '1px dotted var(--border)' }}>{s.buckets}</span>
                      ) : s.buckets}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Test carries the result instead of sitting next to a
                          separate badge. Reaching the server is the question
                          this screen exists to answer, and as a ghost button
                          beside two icons it read as the least important thing
                          in the row. One control now both asks and answers, and
                          clicking it again re-tests. */}
                      <button className="btn btn-sm" disabled={testing === s.id}
                              onClick={() => test(s)}
                              title={t?.error ?? (t?.ok ? 'Reachable — click to test again' : 'Check that this server answers with these credentials')}
                              style={{
                                gap: 5, marginRight: 6,
                                background: !t ? 'var(--accent-soft)' : t.ok ? 'var(--green-bg)' : 'var(--red-bg)',
                                color: !t ? 'var(--accent-hi)' : t.ok ? 'var(--green)' : 'var(--red)',
                                border: '1px solid ' + (!t ? 'var(--accent-ring)' : t.ok ? 'var(--green)' : 'var(--red)'),
                              }}>
                        {testing === s.id
                          ? <><I.Activity size={13}/> Testing…</>
                          : !t ? <><I.Activity size={13}/> Test</>
                            : t.ok ? <><I.CheckCirc size={13}/> Reachable</>
                              : <><I.XCirc size={13}/> Unreachable</>}
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={() => setEditFor(s)}>
                        <I.Pencil size={13}/>
                      </button>
                      {/* A server with buckets on it cannot be removed — the
                          database refuses it. Offering the button anyway meant
                          the only way to find that out was to click it and read
                          an error, so it is disabled with the reason on hover.
                          The span carries the tooltip because a disabled button
                          fires no mouse events of its own. */}
                      {s.buckets > 0 ? (
                        <span style={{ display: 'inline-block', cursor: 'not-allowed' }}
                              /* Short on purpose — this answers "why is this
                                 off" at a glance. The Buckets cell, which is
                                 the thing in the way, carries the full reason. */
                              data-tip={`Can't remove — ${s.buckets} ${s.buckets === 1 ? 'bucket' : 'buckets'} still here`}>
                          <button className="btn btn-ghost btn-icon btn-sm" disabled>
                            <I.Trash size={13}/>
                          </button>
                        </span>
                      ) : (
                        <button className="btn btn-ghost btn-icon btn-sm" title="Remove" onClick={() => setDeleteFor(s)}>
                          <I.Trash size={13}/>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ServerFormModal open={addOpen} title="Add managed S3 server" initial={EMPTY}
                       onClose={() => setAddOpen(false)}
                       onSave={async (v) => { await createStorageServer(v); toast?.success('Server added', v.name); reload(); }}/>
      <ServerFormModal open={!!editFor} title="Edit managed S3 server"
                       initial={editFor
                         ? { name: editFor.name, endpoint: editFor.endpoint, publicEndpoint: editFor.publicEndpoint,
                             region: editFor.region, rootUser: editFor.rootUser, rootPassword: '', note: editFor.note }
                         : EMPTY}
                       editing
                       onClose={() => setEditFor(null)}
                       onSave={async (v) => {
                         if (!editFor) return;
                         // An untouched password field means "keep the current one".
                         const patch: Partial<StorageServerInput> = { ...v };
                         if (!v.rootPassword) delete patch.rootPassword;
                         await updateStorageServer(editFor.id, patch);
                         toast?.success('Server updated', v.name);
                         reload();
                       }}/>

      <Modal open={!!deleteFor} onClose={() => setDeleteFor(null)} size="sm"
             title="Remove this managed S3 server?"
             footer={
               <>
                 <button className="btn btn-ghost btn-sm" onClick={() => setDeleteFor(null)}>Cancel</button>
                 <button className="btn btn-danger-solid btn-sm" onClick={() => deleteFor && remove(deleteFor)}>Remove</button>
               </>
             }>
        {/* Only reachable for a server with no buckets: the row's button is
            disabled otherwise. A count that has gone stale since the last load
            is still caught by the server, and remove() reloads so the row
            corrects itself. */}
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Removes <span className="mono">{deleteFor?.name}</span> from the list. Nothing on the server
          itself is touched; it simply stops being offered when creating a bucket.
        </p>
      </Modal>
    </div>
  );
}

function ServerFormModal({ open, title, initial, editing = false, onClose, onSave }: {
  open: boolean;
  title: string;
  initial: StorageServerInput;
  editing?: boolean;
  onClose: () => void;
  onSave: (v: StorageServerInput) => Promise<void>;
}) {
  const toast = useToast();
  const [form, setForm] = useState<StorageServerInput>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => { if (open) { setForm(initial); setErr(null); setShowPwd(false); } /* eslint-disable-next-line */ }, [open]);

  const set = (k: keyof StorageServerInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErr(null);
  };

  const endpointErr = endpointProblem('Endpoint', form.endpoint.trim());
  const publicErr = endpointProblem('Public endpoint', (form.publicEndpoint ?? '').trim());
  const userErr = form.rootUser.trim() && form.rootUser.trim().length < MIN_USER
    ? `Root user must be at least ${MIN_USER} characters — the storage server rejects shorter ones` : '';
  const pwdErr = form.rootPassword && form.rootPassword.length < MIN_PASSWORD
    ? `Root password must be at least ${MIN_PASSWORD} characters — the storage server rejects shorter ones` : '';
  const problem = endpointErr || publicErr || userErr || pwdErr;

  const ready = !!form.name.trim() && !!form.endpoint.trim() && !!form.rootUser.trim()
    && (editing || !!form.rootPassword) && !problem;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await onSave({ ...form, name: form.name.trim(), endpoint: form.endpoint.trim() });
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, node: React.ReactNode, hint?: React.ReactNode) => (
    <div style={{ marginBottom: 12 }}>
      <label className="field-label">{label}</label>
      {node}
      {hint && <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>{hint}</div>}
    </div>
  );

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title={title} size="sm"
           subtitle="Saving contacts the server with these details first — a typo is caught here rather than at the first bucket."
           footer={
             <>
               <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
               <button className="btn btn-primary btn-sm" disabled={busy || !ready} onClick={save}
                       title={problem || undefined}>
                 {busy ? 'Checking…' : 'Save'}
               </button>
             </>
           }>
      {field('Name', <input className="input" style={{ width: '100%' }} autoFocus placeholder="eu-central"
                            value={form.name} onChange={set('name')}/>)}
      {field('Endpoint',
        <input className="input input-mono" style={{ width: '100%' }} placeholder="http://minio.minio.svc.cluster.local:9000"
               value={form.endpoint} onChange={set('endpoint')}/>,
        endpointErr
          ? <span className="field-error" style={{ fontSize: 11.5 }}>{endpointErr}</span>
          : 'Where this API server reaches it.')}
      {field('Public endpoint',
        <input className="input input-mono" style={{ width: '100%' }} placeholder="same as endpoint"
               value={form.publicEndpoint ?? ''} onChange={set('publicEndpoint')}/>,
        publicErr
          ? <span className="field-error" style={{ fontSize: 11.5 }}>{publicErr}</span>
          : 'What clients are handed in their credentials. Leave empty when it is the same address.')}
      {field('Region', <input className="input input-mono" style={{ width: '100%' }} placeholder="us-east-1"
                              value={form.region ?? ''} onChange={set('region')}/>)}
      {field('Root user', <input className="input input-mono" style={{ width: '100%' }} autoComplete="off"
                                 value={form.rootUser} onChange={set('rootUser')}/>,
        userErr ? <span className="field-error" style={{ fontSize: 11.5 }}>{userErr}</span> : undefined)}
      {field('Root password',
        <div style={{ position: 'relative' }}>
          <input className="input input-mono" style={{ paddingRight: 32, width: '100%' }}
                 type={showPwd ? 'text' : 'password'} autoComplete="new-password"
                 placeholder={editing ? 'unchanged' : ''}
                 value={form.rootPassword ?? ''} onChange={set('rootPassword')}/>
          <button type="button" className="btn btn-ghost btn-icon btn-sm"
                  title={showPwd ? 'Hide' : 'Show'} onClick={() => setShowPwd((v) => !v)}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', height: 22, padding: '0 4px' }}>
            {showPwd ? <I.EyeOff size={13}/> : <I.Eye size={13}/>}
          </button>
        </div>,
        pwdErr
          ? <span className="field-error" style={{ fontSize: 11.5 }}>{pwdErr}</span>
          : editing
            ? 'Leave empty to keep the current password.'
            : `Admin credentials for the whole server — at least ${MIN_PASSWORD} characters. Stored encrypted and never shown again.`)}
      {field('Note', <input className="input" style={{ width: '100%' }} placeholder="optional"
                            value={form.note ?? ''} onChange={set('note')}/>)}
      {err && <div className="field-error" style={{ fontSize: 12.5 }}>{err}</div>}
    </Modal>
  );
}
